import type { ReactNode } from "react";
import { Cpu } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/format-utils";
import type { ComputeSummary } from "@/lib/api/types";
import {
  COMPUTE_CLOCK_EXPLANATION,
  COMPUTE_CLOCK_QUESTION,
  NOT_MEASURED_LABEL,
} from "./three-clocks-panel";

/**
 * The compute clock for ONE feature or task, as two separate figure sets
 * (PI-SPEC-SW-1297).
 *
 *   FIRST-ATTEMPT SUCCESS      — a complexity signal: what the work took when it
 *                                went right the first time.
 *   TOTAL INCLUDING RETRIES    — a cost and quality signal: everything spent,
 *                                failed attempts included.
 *
 * Failed agent trajectories run 12.6-82.5% longer than successful ones, so total
 * compute measures struggle, not difficulty. The two sets are therefore NEVER
 * combined: no element here adds a first-attempt figure to a total figure, and
 * no dollar or cost-per-item figure is derived from either.
 *
 * NULL means "not measured" and renders NOT_MEASURED_LABEL — never 0 and never
 * a bare dash.
 */

export const FIRST_ATTEMPT_BLOCK_TITLE = "First-attempt success";
export const TOTAL_BLOCK_TITLE = "Total including retries";

export const FIRST_ATTEMPT_BLOCK_EXPLANATION =
  "What attempt 0 took when it succeeded. Read as a complexity signal.";

export const TOTAL_BLOCK_EXPLANATION =
  "Everything recorded across all attempts, failed ones included. Read as a cost and quality " +
  "signal, not as difficulty.";

export const FIRST_ATTEMPT_FAILED_NOTE =
  "The first attempt failed, so there is no first-attempt-success figure for this item.";

export const FIRST_ATTEMPT_NOT_RECORDED_NOTE =
  "Attempt 0 was never recorded, so there is no first-attempt-success figure for this item.";

export const NO_ATTEMPTS_HINT =
  "No attempts have been recorded for this item. Compute is recorded per attempt by the " +
  "orchestrator or the spectree-companion mod.";

/** Format a count, or null when not measured. */
function formatCount(value: number | null): string | null {
  return value === null ? null : value.toLocaleString();
}

/**
 * Format in-turn wall clock (milliseconds), or null when not measured.
 *
 * Values of a minute or more go through `formatDuration` (which takes
 * minutes). Shorter values render in seconds, because `formatDuration` would
 * round a measured sub-minute value down to "0m" — and a measured value must
 * never read as zero.
 */
function formatInTurn(milliseconds: number | null): string | null {
  if (milliseconds === null) return null;
  if (milliseconds < 60_000) {
    return `${Number((milliseconds / 1000).toFixed(1)).toString()}s`;
  }
  return formatDuration(milliseconds / 60_000);
}

function formatOutcome(succeeded: boolean | null): string | null {
  if (succeeded === null) return null;
  return succeeded ? "Succeeded" : "Failed";
}

interface FigureRowProps {
  label: string;
  /** Formatted value, or null to render the explicit not-measured state. */
  value: string | null;
  testId: string;
}

function FigureRow({ label, value, testId }: FigureRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1" data-testid={testId}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "text-right text-sm font-medium tabular-nums",
          value === null && "font-normal text-muted-foreground"
        )}
      >
        {value ?? NOT_MEASURED_LABEL}
      </dd>
    </div>
  );
}

interface FigureBlockProps {
  title: string;
  explanation: string;
  note?: string | null;
  testId: string;
  children: ReactNode;
}

function FigureBlock({ title, explanation, note, testId, children }: FigureBlockProps) {
  return (
    <div
      className="flex flex-1 flex-col gap-1.5 rounded-xl border border-border/60 p-3"
      data-testid={testId}
    >
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h4>
      <p className="text-xs leading-relaxed text-muted-foreground">{explanation}</p>
      {note ? <p className="text-xs leading-relaxed text-amber-600 dark:text-amber-400">{note}</p> : null}
      <dl className="divide-y divide-border/40">{children}</dl>
    </div>
  );
}

export interface ComputeFiguresProps {
  summary: ComputeSummary | undefined;
  isLoading?: boolean;
  error?: unknown;
  className?: string;
}

/**
 * Two labelled blocks — "First-attempt success" and "Total including
 * retries" — for one item. An item with zero attempts renders a single
 * not-measured state instead of two blocks of empty rows.
 */
export function ComputeFigures({ summary, isLoading, error, className }: ComputeFiguresProps) {
  let body: ReactNode;

  if (isLoading) {
    body = <p className="text-sm text-muted-foreground">Loading compute...</p>;
  } else if (error) {
    body = (
      <p className="text-sm text-destructive" data-testid="compute-error">
        Failed to load compute
      </p>
    );
  } else if (!summary || summary.totalAttempts === 0) {
    body = (
      <div className="rounded-xl border border-dashed border-border/60 p-3" data-testid="compute-not-measured">
        <p className="text-sm text-muted-foreground">{NOT_MEASURED_LABEL}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{NO_ATTEMPTS_HINT}</p>
      </div>
    );
  } else {
    const firstAttemptNote = !summary.firstAttemptRecorded
      ? FIRST_ATTEMPT_NOT_RECORDED_NOTE
      : summary.firstAttemptSucceeded === false
        ? FIRST_ATTEMPT_FAILED_NOTE
        : null;

    const attemptsLabel =
      `${summary.totalAttempts.toLocaleString()} total, ` +
      `${summary.totalSucceededAttempts.toLocaleString()} succeeded, ` +
      `${summary.totalFailedAttempts.toLocaleString()} failed`;

    const coverageLabel =
      `${summary.totalAttemptsWithTokensRecorded.toLocaleString()} of ` +
      `${summary.totalAttempts.toLocaleString()} attempts carried token measurements`;

    body = (
      <div className="flex flex-col gap-2 md:flex-row">
        <FigureBlock
          title={FIRST_ATTEMPT_BLOCK_TITLE}
          explanation={FIRST_ATTEMPT_BLOCK_EXPLANATION}
          note={firstAttemptNote}
          testId="compute-first-attempt"
        >
          <FigureRow
            label="Attempt 0 outcome"
            value={formatOutcome(summary.firstAttemptSucceeded)}
            testId="compute-first-attempt-outcome"
          />
          <FigureRow
            label="Tokens in"
            value={formatCount(summary.firstAttemptSuccessTokensIn)}
            testId="compute-first-attempt-tokens-in"
          />
          <FigureRow
            label="Tokens out"
            value={formatCount(summary.firstAttemptSuccessTokensOut)}
            testId="compute-first-attempt-tokens-out"
          />
          <FigureRow
            label="Steps"
            value={formatCount(summary.firstAttemptSuccessSteps)}
            testId="compute-first-attempt-steps"
          />
          <FigureRow
            label="In-turn time"
            value={formatInTurn(summary.firstAttemptSuccessInTurnMilliseconds)}
            testId="compute-first-attempt-in-turn"
          />
          <FigureRow
            label="Model"
            value={summary.firstAttemptSuccessModel}
            testId="compute-first-attempt-model"
          />
        </FigureBlock>

        <FigureBlock
          title={TOTAL_BLOCK_TITLE}
          explanation={TOTAL_BLOCK_EXPLANATION}
          testId="compute-total"
        >
          <FigureRow label="Attempts" value={attemptsLabel} testId="compute-total-attempts" />
          <FigureRow
            label="Tokens in"
            value={formatCount(summary.totalTokensIn)}
            testId="compute-total-tokens-in"
          />
          <FigureRow
            label="Tokens out"
            value={formatCount(summary.totalTokensOut)}
            testId="compute-total-tokens-out"
          />
          <FigureRow
            label="Steps"
            value={formatCount(summary.totalSteps)}
            testId="compute-total-steps"
          />
          <FigureRow
            label="In-turn time"
            value={formatInTurn(summary.totalInTurnMilliseconds)}
            testId="compute-total-in-turn"
          />
          <FigureRow
            label="Models recorded"
            value={summary.modelsRecorded.length > 0 ? summary.modelsRecorded.join(", ") : null}
            testId="compute-total-models"
          />
          <p className="pt-1.5 text-xs text-muted-foreground" data-testid="compute-total-coverage">
            {coverageLabel}
          </p>
        </FigureBlock>
      </div>
    );
  }

  return (
    <section
      className={cn("rounded-2xl border border-border/60 p-3", className)}
      data-testid="compute-figures"
      aria-labelledby="compute-figures-heading"
    >
      <div className="mb-1 flex items-center gap-1.5">
        <Cpu className="h-3.5 w-3.5 text-sky-400" aria-hidden="true" />
        <h3 id="compute-figures-heading" className="text-sm font-medium">
          Compute
        </h3>
      </div>
      <p className="text-xs font-medium">{COMPUTE_CLOCK_QUESTION}</p>
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{COMPUTE_CLOCK_EXPLANATION}</p>
      {body}
    </section>
  );
}
